/**
 * Mute list (#2575): add by account id + reason, remove, both behind
 * confirm dialogs. A mute silences a named customer; it does not recall
 * mail already sent.
 */
import { useState } from 'react';
import type { CodexCapacityAlertSuppression } from '@nadoutong/contracts';
import { useOpsUserLabel } from '../../api/directory.ts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';

interface Props {
  readonly suppressions: readonly CodexCapacityAlertSuppression[];
  readonly adding: boolean;
  readonly removingAccountId: string | null;
  readonly onAdd: (accountId: string, reason: string | null) => void;
  readonly onRemove: (accountId: string) => void;
}

export function CodexCapacitySuppressionsPanel({
  suppressions,
  adding,
  removingAccountId,
  onAdd,
  onRemove,
}: Props): JSX.Element {
  const t = useT();
  const opsUserLabel = useOpsUserLabel();
  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [pendingAdd, setPendingAdd] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const trimmedId = accountId.trim();

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-suppressions-semantics">
        {t('codex_capacity.suppressions.semantics')}
      </p>

      <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <FormField label={t('codex_capacity.suppressions.account_id')} fieldId="codex-capacity-mute-account">
          <Input
            id="codex-capacity-mute-account"
            value={accountId}
            onChange={(event) => setAccountId(event.currentTarget.value)}
            data-testid="codex-capacity-mute-account"
          />
        </FormField>
        <FormField label={t('codex_capacity.suppressions.reason')} fieldId="codex-capacity-mute-reason">
          <Input
            id="codex-capacity-mute-reason"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            data-testid="codex-capacity-mute-reason"
          />
        </FormField>
        <Button
          disabled={trimmedId.length === 0}
          onClick={() => setPendingAdd(true)}
          data-testid="codex-capacity-mute-add"
        >
          {t('codex_capacity.suppressions.add')}
        </Button>
      </div>

      <Table testId="codex-capacity-suppressions-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('codex_capacity.suppressions.column.account')}</Th>
            <Th>{t('codex_capacity.suppressions.column.reason')}</Th>
            <Th>{t('codex_capacity.suppressions.column.created')}</Th>
            <Th>{t('codex_capacity.suppressions.column.by')}</Th>
            <Th>{t('codex_capacity.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {suppressions.length === 0 ? (
            <TableEmpty colSpan={5}>{t('codex_capacity.suppressions.empty')}</TableEmpty>
          ) : (
            suppressions.map((row) => (
              <Tr
                key={row.account_id}
                interactive={false}
                data-testid={`codex-capacity-suppression-row-${row.account_id}`}
              >
                <Td emphasis>
                  <span className="flex flex-col">
                    <span>{row.account_email ?? row.account_id}</span>
                    <span className="font-mono text-micro text-paper-low">{row.account_id}</span>
                  </span>
                </Td>
                <Td>{row.reason ?? '—'}</Td>
                <Td mono>{formatLocalTimestamp(row.created_at, { seconds: true })}</Td>
                <Td>{opsUserLabel(row.created_by_ops_user_id) || row.created_by_ops_user_id}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={removingAccountId === row.account_id}
                    onClick={() => setPendingRemove(row.account_id)}
                    data-testid={`codex-capacity-unmute-${row.account_id}`}
                  >
                    {t('codex_capacity.suppressions.remove')}
                  </Button>
                </Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>

      <ConfirmDialog
        open={pendingAdd}
        onOpenChange={(open) => {
          if (!open) setPendingAdd(false);
        }}
        tone="danger"
        title={t('codex_capacity.suppressions.confirm_title', { account: trimmedId })}
        body={
          <p
            className="m-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright"
            data-testid="codex-capacity-mute-confirm-consequence"
          >
            {t('codex_capacity.suppressions.confirm_consequence')}
          </p>
        }
        confirmLabel={t('codex_capacity.suppressions.confirm_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={adding}
        onConfirm={() => {
          if (trimmedId.length === 0) return;
          onAdd(trimmedId, reason.trim().length > 0 ? reason.trim() : null);
          setPendingAdd(false);
          setAccountId('');
          setReason('');
        }}
        testId="codex-capacity-mute-confirm"
      />

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title={t('codex_capacity.suppressions.restore_title', { account: pendingRemove ?? '' })}
        body={
          <p
            className="m-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-dense text-warn"
            data-testid="codex-capacity-unmute-confirm-consequence"
          >
            {t('codex_capacity.suppressions.restore_consequence')}
          </p>
        }
        confirmLabel={t('codex_capacity.suppressions.restore_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={removingAccountId !== null}
        onConfirm={() => {
          if (pendingRemove === null) return;
          onRemove(pendingRemove);
          setPendingRemove(null);
        }}
        testId="codex-capacity-unmute-confirm"
      />
    </div>
  );
}
